export enum ServiceLine {
  ALLERGY_AND_IMMUNOLOGY = "Allergy & Immunology",
  APPLIED_BEHAVIORAL_ANALYSIS_THERAPY = "Applied Behavioral Analysis Therapy",
  ART_THERAPY = "Art Therapy",
  AUDIOLOGY = "Audiology",
  BEHAVIORAL_HEALTH = "Behavioral Health",
  CARDIAC_REHAB = "Cardiac Rehab",
  CARDIOLOGY = "Cardiology",
  DERMATOLOGY = "Dermatology",
  ELECTROPHYSIOLOGY = "Electrophysiology",
  ENDOCRINOLOGY = "Endocrinology",
  ENT = "ENT",
  FEEDING_TUBE_THERAPY = "Feeding Tube Therapy",
  GASTROENTEROLOGY = "Gastroenterology",
  HOME_HEALTH = "Home Health",
  HYPERTENSION = "Hypertension",
  INFECTIOUS_DISEASE = "Infectious Disease",
  LABORATORY = "Laboratory",
  MED_MANAGEMENT = "Medication Management",
  MED_MANAGEMENT_AND_THERAPY = "Medication Management and Therapy",
  MUSIC_THERAPY = "Music Therapy",
  NEPHROLOGY = "Nephrology",
  NEUROLOGY = "Neurology",
  OCCUPATIONAL_THERAPY = "Occupational Therapy",
  OPHTHALMOLOGY = "Ophthalmology",
  ORTHOPEDICS = "Orthopedics",
  OTHER = "Other",
  PHLEBOTOMY = "Phlebotomy",
  PHYSICAL_THERAPY = "Physical Therapy",
  PODIATRY = "Podiatry",
  PPEC = "PPEC",
  PRIMARY_CARE = "Primary Care",
  PULMONOLOGY = "Pulmonology",
  RADIOLOGY = "Radiology",
  RESPIRATORY_THERAPY = "Respiratory Therapy",
  RHEUMATOLOGY = "Rheumatology",
  SLEEP = "Sleep",
  SPEECH_THERAPY = "Speech Therapy",
  THERAPY = "Therapy",
  TMS = "TMS",
  DEMENTIA = "Dementia",
  IOP = "IOP",
  PSYCH_TESTING = "Psych Testing",
  NOT_INDICATED = "Not Indicated",
  HOSPITAL_DISCHARGE = "Hospital Discharge",
  URGENT_CARE = "Urgent Care",
  UROLOGY = "Urology",
  ANGIO = "Angio",
  ARTERIAL = "Arterial",
  CELLULITIS = "Cellulitis",
  COLD_LOWER_EXTREMITIES = "Cold or discolored lower extremities",
  DEEP_VEIN = "Deep Vein",
  DIALYSIS = "Dialysis",
  DVT = "DVT",
  FIBROIDS = "Fibroids",
  GAE = "GAE",
  GANGRENE = "Gangrene",
  HAE = "HAE",
  PAE = "PAE",
  PEVD = "PeVD",
  PFE = "PFE",
  R_O_DVT = "R/O DVT",
  SORES = "Sores",
  UFE = "UFE",
  ULCERS = "Ulcers",
  VASCULAR = "Vascular",
  VEIN = "Vein",
  WOUND = "Wound",
  OUT_OF_SCOPE = "Out of Scope",
  NONE = "None",
  SINUS = "Sinus",
  TONSIL = "Tonsil",
}

export const DEFAULT_SERVICE_LINES = Object.values(ServiceLine) as ServiceLine[];

export const ServiceLineDescriptions: Record<ServiceLine, string> = {
  [ServiceLine.ALLERGY_AND_IMMUNOLOGY]:
    "A service line for allergy and immunology would be referencing a patient's allergy and immunology history.",
  [ServiceLine.APPLIED_BEHAVIORAL_ANALYSIS_THERAPY]:
    "A service line for Applied Behavioral Analysis (ABA) Therapy would be referencing a patient receiving behavioral therapy, often for autism spectrum disorder or other developmental conditions.",
  [ServiceLine.ART_THERAPY]:
    "A service line for Art Therapy would be referencing a patient receiving therapeutic art-based interventions.",
  [ServiceLine.AUDIOLOGY]: "A service line for audiology would be referencing a patient's hearing history.",
  [ServiceLine.BEHAVIORAL_HEALTH]:
    "A service line for behavioral health would be referencing a patient's mental health history.",
  [ServiceLine.CARDIAC_REHAB]:
    "A service line for cardiac rehab would be referencing a patient's cardiac rehabilitation program history (supervised exercise, education, and monitoring after a cardiac event or procedure).",
  [ServiceLine.CARDIOLOGY]: "A service line for cardiology would be referencing a patient's heart history.",
  [ServiceLine.DERMATOLOGY]: "A service line for dermatology would be referencing a patient's skin history.",
  [ServiceLine.ELECTROPHYSIOLOGY]:
    "A service line for electrophysiology would be referencing a patient's heart rhythm or electrical conduction history (e.g., arrhythmia, ablation, pacemaker/ICD).",
  [ServiceLine.ENDOCRINOLOGY]: "A service line for endocrinology would be referencing a patient's endocrine history.",
  [ServiceLine.ENT]: "A service line for ENT would be referencing a patient's ear, nose, and throat history.",
  [ServiceLine.FEEDING_TUBE_THERAPY]:
    "A service line for Feeding Tube Therapy would be referencing a patient receiving feeding-tube-related care or therapy (e.g., G-tube/NG-tube feeding support).",
  [ServiceLine.GASTROENTEROLOGY]:
    "A service line for gastroenterology would be referencing a patient's digestive system history.",
  [ServiceLine.HOME_HEALTH]:
    "A service line for home health would be referencing a patient's home-based medical care history.",
  [ServiceLine.HYPERTENSION]: "A service line for hypertension would be referencing care for high blood pressure.",
  [ServiceLine.INFECTIOUS_DISEASE]:
    "A service line for infectious disease would be referencing a patient's infectious disease history.",
  [ServiceLine.LABORATORY]: "A service line for laboratory would be referencing a patient's laboratory history.",
  [ServiceLine.MED_MANAGEMENT]:
    "A service line for medication management would be referencing a patient's medication history.",
  [ServiceLine.MED_MANAGEMENT_AND_THERAPY]:
    "A service line for medication management and therapy would be referencing a patient's medication and therapy history.",
  [ServiceLine.MUSIC_THERAPY]:
    "A service line for Music Therapy would be referencing a patient receiving therapeutic music-based interventions.",
  [ServiceLine.NEPHROLOGY]: "A service line for nephrology would be referencing a patient's kidney care.",
  [ServiceLine.NEUROLOGY]: "A service line for neurology would be referencing a patient's nervous system history.",
  [ServiceLine.OCCUPATIONAL_THERAPY]:
    "A service line for occupational therapy would be referencing a patient's occupational therapy history.",
  [ServiceLine.OPHTHALMOLOGY]: "A service line for ophthalmology would be referencing a patient's eye history.",
  [ServiceLine.ORTHOPEDICS]: "A service line for orthopedics would be referencing a patient's bone and joint history.",
  [ServiceLine.OTHER]: "A service line for other would be referencing a patient's other history.",
  [ServiceLine.PHLEBOTOMY]: "A service line for phlebotomy would be referencing a patient's blood history.",
  [ServiceLine.PHYSICAL_THERAPY]:
    "A service line for physical therapy would be referencing a patient's physical therapy history.",
  [ServiceLine.PODIATRY]: "A service line for podiatry would be referencing a patient's foot history.",
  [ServiceLine.PPEC]:
    "A service line for PPEC (Prescribed Pediatric Extended Care) would be referencing a pediatric patient receiving extended nursing or therapeutic services at a PPEC center or via PPEC home health.",
  [ServiceLine.PRIMARY_CARE]: "A service line for primary care would be referencing a patient's primary care history.",
  [ServiceLine.PULMONOLOGY]: "A service line for pulmonology would be referencing a patient's lung history.",
  [ServiceLine.RADIOLOGY]: "A service line for radiology would be referencing a patient's imaging history.",
  [ServiceLine.RESPIRATORY_THERAPY]:
    "A service line for respiratory therapy would be referencing a patient's respiratory therapy history.",
  [ServiceLine.RHEUMATOLOGY]:
    "A service line for rheumatology would be referencing a patient's joint and muscle history.",
  [ServiceLine.SLEEP]: "A service line for sleep would be referencing a patient's sleep history.",
  [ServiceLine.SPEECH_THERAPY]:
    "A service line for speech therapy would be referencing a patient's speech therapy history.",
  [ServiceLine.THERAPY]: "A service line for therapy would be referencing a patient's therapy history.",
  [ServiceLine.TMS]: "A service line for TMS would be referencing a patient's TMS history.",
  [ServiceLine.DEMENTIA]:
    "A service line for Dementia would be referencing a patient with cognitive decline, memory loss, or a diagnosis of dementia or Alzheimer's.",
  [ServiceLine.IOP]:
    "A service line for IOP (Intensive Outpatient Program) would be referencing a patient referred for an intensive outpatient behavioral health program.",
  [ServiceLine.PSYCH_TESTING]:
    "A service line for Psych Testing would be referencing a patient referred for psychological or neuropsychological testing.",
  [ServiceLine.NOT_INDICATED]:
    "A service line for Not Indicated would be used when the referral does not specify a service line.",
  [ServiceLine.HOSPITAL_DISCHARGE]:
    "A service line for Hospital Discharge would be referencing a patient being discharged from an inpatient hospital stay and referred for outpatient follow-up.",
  [ServiceLine.URGENT_CARE]: "A service line for urgent care would be referencing a patient's urgent care history.",
  [ServiceLine.UROLOGY]: "A service line for urology would be referencing a patient's urinary system history.",
  [ServiceLine.ANGIO]:
    "A service line for Angiography/Angioplasty would be referencing a patient's vascular imaging or intervention history.",
  [ServiceLine.ARTERIAL]: `A service line related to arterial care refers to a patient's history of artery-related conditions.
   When identifying relevant documents or data, look for terms such as ABI, atherosclerosis, coronary artery disease, decreased pedal pulses, PAD, peripheral arterial disease, or other indications of arterial involvement.`,
  [ServiceLine.CELLULITIS]: "A service line for Cellulitis would be referencing a patient's skin infection history.",
  [ServiceLine.COLD_LOWER_EXTREMITIES]:
    "A service line for cold or discolored lower extremities would be referencing a patient with symptoms of poor circulation in the legs or feet.",
  [ServiceLine.DEEP_VEIN]: "A service line for deep vein would be referencing a patient's deep vein history.",
  [ServiceLine.DIALYSIS]: "A service line for dialysis would be referencing a patient's dialysis history.",
  [ServiceLine.DVT]:
    "A service line for Deep Vein Thrombosis (DVT) would be referencing a patient with a blood clot in a deep vein.",
  [ServiceLine.FIBROIDS]: "A service line for Fibroids would be referencing a patient's uterine fibroids history.",
  [ServiceLine.GAE]:
    "A service line for Genicular Artery Embolization (GAE) would be referencing a patient's genicular artery embolization history.",
  [ServiceLine.GANGRENE]:
    "A service line for Gangrene would be referencing a patient with tissue death due to infection or lack of blood supply.",
  [ServiceLine.HAE]:
    "A service line for Hepatic Artery Embolization (HAE) would be referencing a patient's hepatic artery embolization history.",
  [ServiceLine.PAE]:
    "A service line for Prostatic Artery Embolization (PAE) would be referencing a patient's prostatic artery embolization history.",
  [ServiceLine.PFE]:
    "A service line for Plantar Fasciitis Embolization (PFE) would be referencing a patient's plantar fasciitis embolization history.",
  [ServiceLine.PEVD]:
    "A service line for Peripheral Endovascular Disease (PeVD) would be referencing a patient's peripheral endovascular disease history.",
  [ServiceLine.R_O_DVT]:
    "A service line for Rule Out Deep Vein Thrombosis (R/O DVT) would be referencing a patient's rule out deep vein thrombosis history.",
  [ServiceLine.UFE]:
    "A service line for Uterine Fibroid Embolization (UFE) would be referencing a patient's uterine fibroid embolization history.",
  [ServiceLine.SORES]:
    "A service line for Sores would be referencing a patient with open sores or lesions requiring vascular evaluation.",
  [ServiceLine.ULCERS]:
    "A service line for Ulcers would be referencing a patient with chronic ulcers requiring vascular evaluation.",
  [ServiceLine.VASCULAR]:
    "A service line for Vascular would be referencing a patient's blood vessel or vascular system history.",
  [ServiceLine.VEIN]: "A service line for Vein would be referencing a patient's vein history.",
  [ServiceLine.WOUND]: "A service line for Wound Care would be when specifically requesting referral for wound care",
  [ServiceLine.OUT_OF_SCOPE]:
    "A service line for Out of Scope would be for a service out of the scope of the practice.",
  [ServiceLine.NONE]: "A service line for None would be for a service that is not applicable, this is default",
  [ServiceLine.SINUS]: "A service line for Sinus would be referencing a patient's sinus history.",
  [ServiceLine.TONSIL]: "A service line for Tonsil would be referencing a patient's tonsil history.",
};

export const SERVICE_LINE_PROMPT = `Service line. Use these descriptions to identify the correct service line:\n${Object.entries(
  ServiceLineDescriptions
)
  .map(([key, description]) => `- ${key}: ${description.replace(/\s+/g, " ").trim()}`)
  .join("\n")}`;
